'use client'

import { useId, useRef, useState } from 'react'

import {
  faArrowUpFromBracket,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { Colors, Typography } from '@b-stock/fe-core/design-system'

const DEFAULT_MAX_BYTES = 10 * 1024 * 1024 // 10 MB

const Dropzone = styled.button<{ $dragging: boolean }>`
  ${Typography.Body2}
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 1.5rem;
  border: 0.0625rem dashed
    ${({ $dragging }) =>
      $dragging
        ? Colors.Semantic.BStock.default.lightBackground
        : Colors.Semantic.Neutral.medium_grey};
  border-radius: 0.3125rem;
  background: ${({ $dragging }) =>
    $dragging
      ? Colors.Semantic.BStock.background.lightBackground
      : 'transparent'};
  color: ${Colors.Semantic.Neutral.dark_grey};
  cursor: pointer;

  &:hover {
    border-color: ${Colors.Semantic.BStock.onhover};
  }
`

const DropzoneDescription = styled.span`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};
`

const HiddenInput = styled.input`
  display: none;
`

const FileList = styled.ul`
  list-style: none;
  margin: 0.75rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const FileRow = styled.li`
  ${Typography.Body2}
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 0.0625rem solid ${Colors.Semantic.Neutral.light_grey};
  border-radius: 0.3125rem;
`

const FileName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  border: none;
  background: none;
  color: ${Colors.Semantic.Neutral.dark_grey};
  cursor: pointer;
  padding: 0.25rem;

  &:hover {
    color: ${Colors.Semantic.Error.highlight};
  }
`

const ErrorText = styled.div`
  ${Typography.Body2}
  color: ${Colors.Semantic.Error.highlight};
  padding-top: 0.5rem;
`

type FileUploadProps = {
  /** Currently-selected files (controlled). */
  files: File[]
  onChange: (files: File[]) => void
  /** `accept` attribute, e.g. ".pdf,.png,.jpg" or "image/*,application/pdf". */
  accept?: string
  /** Allow selecting more than one file. Default false. */
  multiple?: boolean
  /** Max size per file in bytes. Default 10 MB. */
  maxBytes?: number
  label?: string
  /** Optional secondary line rendered inside the dropzone, below the label. */
  description?: string
  disabled?: boolean
}

/**
 * Minimal accessible file-upload control: click-or-drop dropzone, selected-file
 * list with remove, and per-file size/type validation. fe-core ships no dropzone,
 * so this is the home-portal primitive used by the buying-eligibility and
 * address-book document flows.
 */
const FileUpload = ({
  files,
  onChange,
  accept,
  multiple = false,
  maxBytes = DEFAULT_MAX_BYTES,
  label,
  description,
  disabled = false,
}: FileUploadProps) => {
  const { formatMessage } = useIntl()
  const inputRef = useRef<HTMLInputElement>(null)
  const inputId = useId()
  const [dragging, setDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const resolvedLabel =
    label ?? formatMessage({ id: 'Common.fileUpload.dropHere' })

  const validate = (file: File): string | null => {
    if (file.size > maxBytes) {
      const mb = Math.round(maxBytes / (1024 * 1024))
      return formatMessage(
        { id: 'Common.fileUpload.sizeLimit' },
        { fileName: file.name, maxMb: mb }
      )
    }
    return null
  }

  const addFiles = (incoming: FileList | null) => {
    if (!incoming || incoming.length === 0) return
    const accepted: File[] = []
    for (const file of Array.from(incoming)) {
      const err = validate(file)
      if (err) {
        setError(err)
        return
      }
      accepted.push(file)
    }
    setError(null)
    onChange(multiple ? [...files, ...accepted] : accepted.slice(0, 1))
  }

  const removeFile = (index: number) => {
    onChange(files.filter((_, i) => i !== index))
  }

  return (
    <div>
      <Dropzone
        type="button"
        $dragging={dragging}
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          if (!disabled) setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          if (!disabled) addFiles(e.dataTransfer.files)
        }}
        aria-describedby={error ? `${inputId}-error` : undefined}
      >
        <FontAwesomeIcon icon={faArrowUpFromBracket} />
        {resolvedLabel}
        {description && (
          <DropzoneDescription>{description}</DropzoneDescription>
        )}
      </Dropzone>
      <HiddenInput
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={(e) => {
          addFiles(e.target.files)
          // Reset so selecting the same file again re-fires onChange.
          e.target.value = ''
        }}
      />
      {files.length > 0 && (
        <FileList>
          {files.map((file, index) => (
            <FileRow key={`${file.name}-${file.size}-${file.lastModified}`}>
              <FileName>{file.name}</FileName>
              <RemoveButton
                type="button"
                onClick={() => removeFile(index)}
                aria-label={formatMessage(
                  { id: 'Common.fileUpload.remove' },
                  { fileName: file.name }
                )}
              >
                <FontAwesomeIcon icon={faXmark} />
              </RemoveButton>
            </FileRow>
          ))}
        </FileList>
      )}
      {error && <ErrorText id={`${inputId}-error`}>{error}</ErrorText>}
    </div>
  )
}

export default FileUpload
